import { Select as AntdSelect, GlobalToken } from 'antd';
import { isNil } from 'lodash';
import styled, { createGlobalStyle } from 'styled-components';

import EPInput from '@/comp/EPInput';
import { CustomThemeToken } from '@/theme';

export const getTableCss = token => `
  .table {
    margin-bottom: ${token.marginLG}px;

    .ant-table {
      background-color: ${token.colorBgContainer};

      .ant-table-thead > tr > td {
        background-color: ${token.colorBgElevated};
      }

      .ant-table-container table > thead > tr:first-child > *:last-child {
        border-radius: 0;
      }

      .ant-table-container table > thead > tr:first-child > *:first-child{
        border-radius: 0;
      }

      .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
          [colspan]
        )::before {
        display: none;
      }

      .ant-table-tbody > tr.ant-table-row:hover > td {
        background: inherit;
      }

      .ant-table-tbody > tr > td {
        border-top: none;
        border-bottom: 1px solid ${token.colorBorder};
      }

      .ant-table-thead > tr > td {
        border-bottom: 2px solid ${token.colorBorder};
      }

      .ant-table-tbody > tr:last-child > td {
        border-bottom: 1px solid ${token.colorBorder};
      }

      .ant-table-tbody > tr.ant-table-row:last-child:hover > td {
        border-bottom-color: ${token.colorBorder};
      }

      .ant-table-cell-scrollbar {
        visibility: collapse;
      }

      .ant-table-tbody > tr > td.ant-table-cell-row-hover {
        background: ${token.colorBgContainer};
      }

      .ant-table-cell {
        padding: ${`${token.paddingSM}px ${token.paddingXXS}px`};
        vertical-align: middle;
      }

    }

    .ant-table-placeholder {
      display: none;
    }
  }
`;

interface EngineStepsContainerProps {
  $hasFinishedWizard?: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const EngineStepsContainer = styled.div<EngineStepsContainerProps>`
  padding: ${({ $hasFinishedWizard }) => ($hasFinishedWizard ? '20px 34px 20px 43px' : '40px 34px 34px 43px')};
  height: 100%;
  display: flex;
  flex-direction: column;

  .engine-step-title {
    font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
    margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;
  }

  .flex-row {
    display: flex;
    flex-grow: 1;

    .substeps {
      display: flex;
      flex-direction: column;
      border-right: 1px solid ${({ theme: { token } }) => token.colorBorder};
      white-space: nowrap;

      .cursor-pointer {
        &:hover {
          cursor: pointer;
        }
      }

      .substep {
        display: flex;
        align-items: center;
        margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;

        .substep-circle {
          width: 29px;
          height: 29px;
          border-radius: 29px;
          margin-right: ${({ theme: { token } }) => token.marginXS}px;
          position: relative;
          background-color: ${({ theme: { token } }) => token.colorPrimary};
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .substep-circle-result {
          position: absolute;
          top: 5px;
          left: 5px;
          width: 19px;
          height: 19px;
          border-radius: 19px;
          background-color: ${({ theme: { token } }) => token.colorSuccess};
          z-index: 100;
        }

        .green-background {
          background-color: ${({ theme: { token } }) => token.colorSuccess};
        }

        .substep-name {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          margin-right: ${({ theme: { token } }) => token.marginLG}px;
          color: ${({ theme: { token } }) => token.colorText};
        }
      }

      .low-opacity {
        opacity: 0.3;
      }
    }

    .selected-substep {
      flex-grow: 1;
    }
  }

  .footer-buttons {
    display: flex;
    align-self: flex-end;
    justify-content: space-between;

    .back-button {
      margin-right: ${({ theme: { token } }) => token.marginSM}px;
    }
  }
`;

export const PopconfirmDeleteItemGlobalStyle = createGlobalStyle`
  &.ant-popconfirm .ant-popconfirm-message {
    align-items: center;
  }
`;

export const DeleteItemPopconfirmIconContainer = styled.div`
  .delete-item-icon {
    cursor: pointer;
  }
`;

export const TableHeaderCellContainer = styled.span`
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ValidationMessagePopoverGlobalStyle = createGlobalStyle<{ theme?: { token: CustomThemeToken } }>`
  &.ant-popover .ant-popover-title {
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }

  .validation-message-title {
    margin-left: ${({ theme: { token } }) => token.marginSM}px;
  }
`;

interface InputProps {
  $valueLength?: number;
  bordered: boolean;
  theme?: {
    token: GlobalToken;
  };
}

export const Input = styled(EPInput)<InputProps>`
  &.ant-input {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    padding-left: 0;
    padding-right: 0;
    height: 21px;
    ${({ $valueLength }) => !isNil($valueLength) && `width: calc(${$valueLength}ch + 1ch);`}

    &:first-child {
      margin-top: 0;
    }

    ${({ bordered }) =>
      bordered &&
      `
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      border-radius: 2px;
      border: 0.5px solid ${({ theme: { token } }) => token.colorBorder};
      width: 43px;
      margin-right: ${({ theme: { token } }) => token.marginSM}px;
      padding-left: ${({ theme: { token } }) => token.paddingXS}px;
    `}

    &::placeholder {
      color: ${({ theme: { token } }) => token.colorPrimary};
    }
  }
`;

interface SelectProps {
  mode?: string;
  theme: {
    token: GlobalToken;
  };
}

export const Select = styled(AntdSelect)<SelectProps>`
  &.ant-select {
    width: 100%;

    .ant-select-selector {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      border-radius: 2px;
      border: 0.5px solid ${({ theme: { token } }) => token.colorBorder};
      max-height: 200px;
      padding: ${({ theme: { token } }) => token.paddingXS}px;

      .ant-select-selection-item {
        display: flex;
        align-items: center;
        border-radius: 6px;
        background-color: ${({ mode, theme: { token } }) =>
          mode === 'multiple' ? token.colorBgElevated : token.colorBgContainer};
        margin-right: ${({ theme: { token } }) => token.marginXXS}px;
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        height: 21px;
        ${({ mode }) =>
          mode !== 'multiple' &&
          `
          color: ${({ theme: { token } }) => token.colorText};
          padding-bottom: ${({ theme: { token } }) => token.paddingXXS}px;  
        `};

        .ant-select-selection-item-content {
          color: ${({ theme: { token } }) => token.colorTextSecondary};
          padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
          margin-right: ${({ theme: { token } }) => token.marginXS}px;
        }

        .ant-select-selection-item-remove {
          color: ${({ theme: { token } }) => token.colorTextSecondary};
        }
      }

      .ant-select-selection-search {
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }
    }

    .ant-select-arrow {
      color: ${({ theme: { token } }) => token.colorBorder};
    }
  }
`;
